import { ReactElement } from 'react'
import {
  isHotDimensioningVisible,
  isLoadEccentricityVisible,
  isShearForceReductionVisible,
  isSteelSupportVisible,
  isTimberSupportVisible,
  isVibrationVisible,
} from '@editor/components/CheckSettingsFormFields/utils'
import { CheckSettingsSectionKey } from '../MultiEditCheckSettings/constants'
import {
  CheckSettingsMaterial,
  MultiEditCheckSettingsElement,
} from '../MultiEditCheckSettings/types'
import { isCheckSettingsSectionAvailable } from '../MultiEditCheckSettings/utils'
import MultiEditHotDimensioning from '../MultiEditHotDimensioning'
import { hasHotDimensioningSettings } from '../MultiEditHotDimensioning/utils'
import MultiEditLoadEccentricity from '../MultiEditLoadEccentricity'
import { hasLoadEccentricitySettings } from '../MultiEditLoadEccentricity/utils'
import MultiEditShearCheck from '../MultiEditShearCheck'
import { hasShearCheckSettings } from '../MultiEditShearCheck/utils'
import MultiEditSteelBasePlate from '../MultiEditSteelBasePlate'
import { hasSteelBasePlateSettings } from '../MultiEditSteelBasePlate/utils'
import MultiEditSupportCompression from '../MultiEditSupportCompression'
import { hasSupportCompressionSettings } from '../MultiEditSupportCompression/utils'
import MultiEditVibrationCheck from '../MultiEditVibrationCheck'
import { hasVibrationCheckSettings } from '../MultiEditVibrationCheck/utils'

export interface CheckSettingsTab {
  key: CheckSettingsSectionKey
  label: string
  render: (elements: MultiEditCheckSettingsElement[]) => ReactElement
}

interface CheckSettingsTabDefinition extends CheckSettingsTab {
  /** The settings slice the tab edits — a member without it disqualifies the tab. */
  hasSettings: (settings: SettingsOnMember) => boolean
  /** The same per-element-type predicate the single-element detail pane uses. */
  isVisible: (elementType: ElementTypes, material: CheckSettingsMaterial) => boolean
}

/**
 * Order matches the single-element detail pane's check settings tabs so the two
 * surfaces stay recognizable as the same thing.
 */
const CHECK_SETTINGS_TAB_DEFINITIONS: CheckSettingsTabDefinition[] = [
  {
    key: 'supportCompression',
    label: 'Auflagerpressung',
    hasSettings: hasSupportCompressionSettings,
    // Same gate as the single-element pane's Auflagerpressung tab: every timber
    // member has support configs, regardless of element type.
    isVisible: (elementType, material) => isTimberSupportVisible(material),
    render: elements => <MultiEditSupportCompression elements={elements} />,
  },
  {
    key: 'hotDimensioning',
    label: 'Heißbemessung',
    hasSettings: hasHotDimensioningSettings,
    isVisible: isHotDimensioningVisible,
    render: elements => <MultiEditHotDimensioning elements={elements} />,
  },
  {
    key: 'vibration',
    label: 'Schwingung',
    hasSettings: hasVibrationCheckSettings,
    isVisible: isVibrationVisible,
    render: elements => <MultiEditVibrationCheck elements={elements} />,
  },
  {
    key: 'steelConfig',
    label: 'Fußpunkt',
    hasSettings: hasSteelBasePlateSettings,
    isVisible: isSteelSupportVisible,
    render: elements => <MultiEditSteelBasePlate elements={elements} />,
  },
  {
    key: 'shearCheck',
    label: 'Querkraftabminderung',
    hasSettings: hasShearCheckSettings,
    isVisible: isShearForceReductionVisible,
    render: elements => <MultiEditShearCheck elements={elements} />,
  },
  {
    key: 'loadEccentricity',
    label: 'Lastexzentrizität',
    hasSettings: hasLoadEccentricitySettings,
    isVisible: isLoadEccentricityVisible,
    render: elements => <MultiEditLoadEccentricity elements={elements} />,
  },
]

/**
 * The tabs whose settings apply to every selected member. Element types differ
 * per tab — lintels have a Querkraftabminderung but no Heißbemessung, columns the
 * other way round — so a selection can end up with any subset, including none.
 */
export const getAvailableCheckSettingsTabs = ({
  elements,
  memberGuids,
}: {
  elements: MultiEditCheckSettingsElement[]
  memberGuids: string[]
}): CheckSettingsTab[] =>
  CHECK_SETTINGS_TAB_DEFINITIONS.filter(({ hasSettings, isVisible }) =>
    isCheckSettingsSectionAvailable({ elements, memberGuids, hasSettings, isVisible }),
  )
